import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { CreateServiceForm, CreateCourseForm } from './Forms';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ImageUpload, IMAGE_FORMATS } from '../components/ui/ImageUpload';
import { Sparkles, AlertCircle, ChevronRight, Info } from 'lucide-react';
import { lookupPostalCode } from '../data/postalCodes';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Edit Service Page - Wraps CreateServiceForm with pre-loaded data
export function EditServicePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders, isAuthenticated } = useAuthStore();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    const loadService = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/services/${id}`, { headers: getAuthHeaders() });
        setService(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'No se pudo cargar el servicio');
      } finally {
        setLoading(false);
      }
    };
    
    loadService();
  }, [id, getAuthHeaders, isAuthenticated, navigate]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Sparkles className="h-12 w-12 text-gold animate-pulse" style={{ color: '#D4A574' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-red-700 mb-2">Error</h2>
          <p className="text-red-600">{error}</p>
          <button 
            onClick={() => navigate('/profile')}
            className="mt-4 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
          >
            Volver al perfil
          </button>
        </div>
      </div>
    );
  }

  return <CreateServiceForm editMode={true} existingService={service} />;
}


// Edit Course Page - Wraps CreateCourseForm with pre-loaded data
export function EditCoursePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders, isAuthenticated } = useAuthStore();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    const loadCourse = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/courses/${id}`, { headers: getAuthHeaders() });
        setCourse(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'No se pudo cargar el curso');
      } finally {
        setLoading(false);
      }
    };
    
    loadCourse();
  }, [id, getAuthHeaders, isAuthenticated, navigate]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Sparkles className="h-12 w-12 text-gold animate-pulse" style={{ color: '#D4A574' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-red-700 mb-2">Error</h2>
          <p className="text-red-600">{error}</p>
          <button 
            onClick={() => navigate('/profile')}
            className="mt-4 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
          >
            Volver al perfil
          </button>
        </div>
      </div>
    );
  }

  return <CreateCourseForm editMode={true} existingCourse={course} />;
}


// ==================== EDIT TEACHER PAGE ====================
export function EditTeacherPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getAuthHeaders, isAuthenticated, user } = useAuthStore();
  const [teacher, setTeacher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form fields
  const [profileImage, setProfileImage] = useState(null);
  const [bioEn, setBioEn] = useState('');
  const [bioEs, setBioEs] = useState('');
  const [bioIt, setBioIt] = useState('');
  const [specialties, setSpecialties] = useState('');
  const [experience, setExperience] = useState('');
  const [certifications, setCertifications] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [country, setCountry] = useState('España');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [cityAutoFilled, setCityAutoFilled] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    const loadTeacher = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/teachers/${id}`, { headers: getAuthHeaders() });
        const data = res.data;
        setTeacher(data);
        
        // Populate form fields
        setProfileImage(data.images?.[0] || null);
        setBioEs(data.bio?.es || '');
        setBioEn(data.bio?.en || '');
        setBioIt(data.bio?.it || '');
        setSpecialties((data.specialties || []).join(', '));
        setCertifications((data.certifications || []).join(', '));
        setExperience(data.experience_years?.toString() || '');
        setPhone(data.phone || '');
        setWebsite(data.website || '');
        setCountry(data.location?.country || 'España');
        setPostalCode(data.location?.postal_code || '');
        setCity(data.location?.city || '');
      } catch (err) {
        setError(err.response?.data?.detail || 'No se pudo cargar el perfil');
      } finally {
        setLoading(false);
      }
    };
    
    loadTeacher();
  }, [id, getAuthHeaders, isAuthenticated, navigate]);

  // Auto-fill city when postal code changes
  useEffect(() => {
    const isSpain = ['españa', 'spain', 'spagna'].includes(country.toLowerCase().trim());
    if (isSpain && postalCode.length === 5) {
      const result = lookupPostalCode(postalCode);
      if (result.found) {
        setCity(result.city);
        setCityAutoFilled(true);
      }
    } else {
      setCityAutoFilled(false);
    }
  }, [postalCode, country]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!bioEs.trim() || !city.trim() || !country.trim() || !phone.trim()) {
      alert('Por favor completa todos los campos obligatorios');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await axios.put(
        `${API_URL}/api/teachers/${id}`,
        {
          bio: { en: bioEn || bioEs, es: bioEs, it: bioIt || bioEs },
          specialties: specialties.split(',').map(s => s.trim()).filter(Boolean),
          experience_years: parseInt(experience) || 0,
          certifications: certifications.split(',').map(s => s.trim()).filter(Boolean),
          images: profileImage ? [profileImage] : [],
          location: {
            address: '',
            city,
            country,
            postal_code: postalCode,
            latitude: 0,
            longitude: 0,
          },
          phone,
          website: website || null,
        },
        { headers: getAuthHeaders() }
      );
      
      alert(teacher?.is_approved 
        ? 'Cambios guardados. Las modificaciones serán revisadas por un administrador.'
        : 'Cambios guardados correctamente.');
      navigate('/profile');
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al guardar los cambios');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Sparkles className="h-12 w-12 text-gold animate-pulse" style={{ color: '#D4A574' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-red-700 mb-2">Error</h2>
          <p className="text-red-600">{error}</p>
          <button onClick={() => navigate('/profile')} className="mt-4 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600">
            Volver al perfil
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 animate-fade-in" data-testid="edit-teacher-page">
      <button onClick={() => navigate(-1)} className="flex items-center text-secondary mb-6 hover:text-gold">
        <ChevronRight className="h-4 w-4 rotate-180 mr-1" /> Volver
      </button>
      
      <h1 className="font-serif text-3xl font-bold text-primary mb-6">Editar Perfil Profesional</h1>
      
      {teacher?.is_approved && (
        <div className="flex gap-3 p-4 rounded-xl mb-6" style={{ backgroundColor: '#FFF3CD' }}>
          <Info className="w-6 h-6 text-yellow-600 flex-shrink-0" />
          <p className="text-sm text-yellow-800">
            Tu perfil está aprobado. Las modificaciones serán revisadas por un administrador antes de publicarse.
            Hasta entonces, tu perfil actual permanecerá visible.
          </p>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm p-6 space-y-6">
        <ImageUpload
          image={profileImage}
          onImageChange={setProfileImage}
          label="Foto de Perfil"
          aspectRatio={IMAGE_FORMATS.PROFILE.aspectRatio}
          maxSize={IMAGE_FORMATS.PROFILE.maxSize}
          shape="circle"
          placeholder="Cambiar foto"
          folder="teachers/"
        />
        
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Biografía (Español) *</label>
          <Textarea placeholder="Cuéntanos sobre ti..." value={bioEs} onChange={(e) => setBioEs(e.target.value)} rows={4} required />
        </div>
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Biografía (Inglés)</label>
          <Textarea placeholder="Tell us about yourself..." value={bioEn} onChange={(e) => setBioEn(e.target.value)} rows={4} />
        </div>
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Biografía (Italiano)</label>
          <Textarea placeholder="Parlaci di te..." value={bioIt} onChange={(e) => setBioIt(e.target.value)} rows={4} />
        </div>
        
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Especialidades (separadas por coma)</label>
          <Input placeholder="Masaje tántrico, Meditación" value={specialties} onChange={(e) => setSpecialties(e.target.value)} />
        </div>
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Certificaciones (separadas por coma)</label>
          <Input placeholder="Certificado ISTA, Yoga" value={certifications} onChange={(e) => setCertifications(e.target.value)} />
        </div>
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Años de Experiencia</label>
          <Input type="number" placeholder="5" value={experience} onChange={(e) => setExperience(e.target.value)} min={0} />
        </div>
        
        <h3 className="font-semibold text-primary pt-4 border-t">Información de Contacto</h3>
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Teléfono *</label>
          <Input placeholder="+34 612 345 678" value={phone} onChange={(e) => setPhone(e.target.value)} required />
        </div>
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Sitio Web (opcional)</label>
          <Input placeholder="https://tuweb.com" value={website} onChange={(e) => setWebsite(e.target.value)} type="url" />
        </div>
        
        <h3 className="font-semibold text-primary pt-4 border-t">Ubicación</h3>
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">País *</label>
          <Input placeholder="España" value={country} onChange={(e) => setCountry(e.target.value)} required />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-primary mb-2">Código Postal *</label>
            <Input placeholder="38650" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} maxLength={5} required />
          </div>
          <div>
            <label className="block text-sm font-semibold text-primary mb-2">
              Ciudad * {cityAutoFilled && <span className="text-green-600 text-xs">(auto)</span>}
            </label>
            <Input placeholder="Los Cristianos" value={city} onChange={(e) => { setCity(e.target.value); setCityAutoFilled(false); }} required className={cityAutoFilled ? 'bg-green-50' : ''} />
          </div>
        </div>
        
        <Button type="submit" disabled={isSubmitting} className="w-full bg-gold text-white py-3" style={{ backgroundColor: '#D4A574' }}>
          {isSubmitting ? 'Guardando...' : 'Guardar Cambios'}
        </Button>
      </form>
    </div>
  );
}


// ==================== EDIT SCHOOL PAGE ====================
export function EditSchoolPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders, isAuthenticated } = useAuthStore();
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form fields
  const [logoImage, setLogoImage] = useState(null);
  const [nameEn, setNameEn] = useState('');
  const [nameEs, setNameEs] = useState('');
  const [nameIt, setNameIt] = useState('');
  const [descEn, setDescEn] = useState('');
  const [descEs, setDescEs] = useState('');
  const [descIt, setDescIt] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [country, setCountry] = useState('España');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [cityAutoFilled, setCityAutoFilled] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    const loadSchool = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/schools/${id}`, { headers: getAuthHeaders() });
        const data = res.data;
        setSchool(data);
        
        // Populate form fields
        setLogoImage(data.images?.[0] || null);
        setNameEs(data.name?.es || '');
        setNameEn(data.name?.en || '');
        setNameIt(data.name?.it || '');
        setDescEs(data.description?.es || '');
        setDescEn(data.description?.en || '');
        setDescIt(data.description?.it || '');
        setPhone(data.phone || '');
        setWebsite(data.website || '');
        setCountry(data.location?.country || 'España');
        setPostalCode(data.location?.postal_code || '');
        setCity(data.location?.city || '');
      } catch (err) {
        setError(err.response?.data?.detail || 'No se pudo cargar la escuela');
      } finally {
        setLoading(false);
      }
    };
    
    loadSchool();
  }, [id, getAuthHeaders, isAuthenticated, navigate]);

  // Auto-fill city when postal code changes
  useEffect(() => {
    const isSpain = ['españa', 'spain', 'spagna'].includes(country.toLowerCase().trim());
    if (isSpain && postalCode.length === 5) {
      const result = lookupPostalCode(postalCode);
      if (result.found) {
        setCity(result.city);
        setCityAutoFilled(true);
      }
    } else {
      setCityAutoFilled(false);
    }
  }, [postalCode, country]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!nameEs.trim() || !descEs.trim() || !city.trim() || !country.trim() || !phone.trim()) {
      alert('Por favor completa todos los campos obligatorios');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await axios.put(
        `${API_URL}/api/schools/${id}`,
        {
          name: { en: nameEn || nameEs, es: nameEs, it: nameIt || nameEs },
          description: { en: descEn || descEs, es: descEs, it: descIt || descEs },
          phone,
          website: website || null,
          images: logoImage ? [logoImage] : [],
          location: {
            address: '',
            city,
            country,
            postal_code: postalCode,
            latitude: 0,
            longitude: 0,
          },
        },
        { headers: getAuthHeaders() }
      );
      
      alert(school?.is_approved 
        ? 'Cambios guardados. Las modificaciones serán revisadas por un administrador.'
        : 'Cambios guardados correctamente.');
      navigate('/profile');
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al guardar los cambios');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Sparkles className="h-12 w-12 text-gold animate-pulse" style={{ color: '#D4A574' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-red-700 mb-2">Error</h2>
          <p className="text-red-600">{error}</p>
          <button onClick={() => navigate('/profile')} className="mt-4 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600">
            Volver al perfil
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 animate-fade-in" data-testid="edit-school-page">
      <button onClick={() => navigate(-1)} className="flex items-center text-secondary mb-6 hover:text-gold">
        <ChevronRight className="h-4 w-4 rotate-180 mr-1" /> Volver
      </button>
      
      <h1 className="font-serif text-3xl font-bold text-primary mb-6">Editar Perfil Escuela</h1>
      
      {school?.is_approved && (
        <div className="flex gap-3 p-4 rounded-xl mb-6" style={{ backgroundColor: '#FFF3CD' }}>
          <Info className="w-6 h-6 text-yellow-600 flex-shrink-0" />
          <p className="text-sm text-yellow-800">
            Tu escuela está aprobada. Las modificaciones serán revisadas por un administrador antes de publicarse.
            Hasta entonces, tu perfil actual permanecerá visible.
          </p>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm p-6 space-y-6">
        <ImageUpload
          image={logoImage}
          onImageChange={setLogoImage}
          label="Logo de la Escuela"
          aspectRatio={IMAGE_FORMATS.PROFILE.aspectRatio}
          maxSize={IMAGE_FORMATS.PROFILE.maxSize}
          shape="circle"
          placeholder="Cambiar logo"
          folder="schools/"
        />
        
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Nombre (Español) *</label>
          <Input placeholder="Nombre de tu escuela" value={nameEs} onChange={(e) => setNameEs(e.target.value)} required />
        </div>
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Nombre (Inglés)</label>
          <Input placeholder="Your school name" value={nameEn} onChange={(e) => setNameEn(e.target.value)} />
        </div>
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Nombre (Italiano)</label>
          <Input placeholder="Nome della scuola" value={nameIt} onChange={(e) => setNameIt(e.target.value)} />
        </div>
        
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Descripción (Español) *</label>
          <Textarea placeholder="Describe tu escuela..." value={descEs} onChange={(e) => setDescEs(e.target.value)} rows={4} required />
        </div>
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Descripción (Inglés)</label>
          <Textarea placeholder="Describe your school..." value={descEn} onChange={(e) => setDescEn(e.target.value)} rows={4} />
        </div>
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Descripción (Italiano)</label>
          <Textarea placeholder="Descrivi la tua scuola..." value={descIt} onChange={(e) => setDescIt(e.target.value)} rows={4} />
        </div>
        
        <h3 className="font-semibold text-primary pt-4 border-t">Información de Contacto</h3>
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Teléfono *</label>
          <Input placeholder="+34 612 345 678" value={phone} onChange={(e) => setPhone(e.target.value)} required />
        </div>
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">Sitio Web (opcional)</label>
          <Input placeholder="https://tuescuela.com" value={website} onChange={(e) => setWebsite(e.target.value)} type="url" />
        </div>
        
        <h3 className="font-semibold text-primary pt-4 border-t">Ubicación</h3>
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">País *</label>
          <Input placeholder="España" value={country} onChange={(e) => setCountry(e.target.value)} required />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-primary mb-2">Código Postal *</label>
            <Input placeholder="38650" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} maxLength={5} required />
          </div>
          <div>
            <label className="block text-sm font-semibold text-primary mb-2">
              Ciudad * {cityAutoFilled && <span className="text-green-600 text-xs">(auto)</span>}
            </label>
            <Input placeholder="Los Cristianos" value={city} onChange={(e) => { setCity(e.target.value); setCityAutoFilled(false); }} required className={cityAutoFilled ? 'bg-green-50' : ''} />
          </div>
        </div>
        
        <Button type="submit" disabled={isSubmitting} className="w-full bg-gold text-white py-3" style={{ backgroundColor: '#D4A574' }}>
          {isSubmitting ? 'Guardando...' : 'Guardar Cambios'}
        </Button>
      </form>
    </div>
  );
}


export default { EditServicePage, EditCoursePage, EditTeacherPage, EditSchoolPage };
